import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button, Table, Input } from "antd";
import FlexBtn from "../../Components/Common/FlexBtn";
import ReactStartEndDatePicker from "../../Components/Common/ReactStartEndDatePicker";
import { getRequest, postRequest } from "../../services";

const AddClassResult = (props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [commonDate, setCommonDate] = useState('');
    const [commonTotalMarks, setCommonTotalMarks] = useState('');
    const [testType, setTestType] = useState('');
    const [examDate, setExamDate] = useState('');
    const [totalMarks, setTotalMarks] = useState('');


    const [results, setResults] = useState([]);

    useEffect(() => {
        const fetchStudents = async () => {
            setLoading(true);
            try {
                const classSectionId = props.state?.sectionId;
                const subjectId = props.state?.subjectId;
                if (!classSectionId || !subjectId) {
                    setResults([]);
                    setLoading(false);
                    return;
                }
                const response = await getRequest(
                    `/api/ClassAssignment/GetStudentsInClass?classSectionId=${classSectionId}&subjectId=${subjectId}`
                );
                const students = response.data.students || [];
                setResults(
                    students.map(student => ({
                        key: student.id,
                        name: student.name,
                        obtainedMarks: '',
                        attendance: 'Present',
                        remarks: ''
                    }))
                );
            } catch (error) {
                setResults([]);
            }
            setLoading(false);
        };
        fetchStudents();
    }, [props.state?.sectionId, props.state?.subjectId]);

    const handleInputChange = (key, field, value) => {
        if (field === 'totalMarks') {
            setCommonTotalMarks(value);
            setResults(prev => 
                prev.map(result => ({
                    ...result,
                    totalMarks: value
                }))
            );
        } else if (field === 'date') {
            setCommonDate(value);
            setResults(prev => 
                prev.map(result => ({
                    ...result,
                    date: value
                }))
            );
        } else {
            setResults(prev => 
                prev.map(result => 
                    result.key === key ? {...result, [field]: value} : result
                )
            );
        }
    };

    const handleDateChange = (name, date) => {
        const formattedDate = date ? new Date(date).toISOString().split('T')[0] : '';
        setCommonDate(formattedDate);
        setResults(prev => 
            prev.map(result => ({
                ...result,
                date: formattedDate
            }))
        );
    };

    const formatToUTCDateString = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0] + 'T00:00:00Z';
    };

    const handleExamDateChange = (name, date) => {
        const formattedDate = formatToUTCDateString(date);
        setExamDate(formattedDate);
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <span>{text}</span>
        },
        {
            title: 'Obtained Marks',
            dataIndex: 'obtainedMarks',
            key: 'obtainedMarks',
            render: (text, record) => (
                <Input 
                    value={text}
                    onChange={(e) => handleInputChange(record.key, 'obtainedMarks', e.target.value)}
                    placeholder="Enter marks"
                />
            )
        },
        {
            title: 'Attendance',
            dataIndex: 'attendance',
            key: 'attendance',
            render: (text, record) => (
                <select
                    value={text}
                    onChange={(e) => handleInputChange(record.key, 'attendance', e.target.value)}
                    style={{ width: '100%' }}
                >
                    <option value="Present">Present</option>
                    <option value="Absent">Absent</option>
                </select>
            )
        },
        {
            title: 'Remarks',
            dataIndex: 'remarks',
            key: 'remarks',
            render: (text, record) => (
                <Input 
                    value={text}
                    onChange={(e) => handleInputChange(record.key, 'remarks', e.target.value)}
                    placeholder="Enter remarks"
                />
            )
        },
    ];

    const handleSubmitData = async () => {
        setLoading(true);
        try {
            const payload = {
                examType: testType || "",
                examDate: formatToUTCDateString(examDate) || new Date().toISOString().split('T')[0] + 'T00:00:00Z',
                totalMarks: Number(totalMarks) || 0,
                subjectId: props.state?.subjectId || "",
                studentDetails: results.map(result => ({
                    studentId: result.key || "",
                    classAssignmentId: props.state?.classId || "",
                    obtainedMarks: Number(result.obtainedMarks) || 0,
                    attendanceStatus: result.attendance || "",
                    remarks: result.remarks || ""
                }))
            };
            console.log("payload", JSON.stringify(payload, null, 2));
            await postRequest("/api/Grades/add-offline-exam-marks", payload);
            if (props.onRequestClose) {
                props.onRequestClose();
            }
            // Optionally handle success (e.g., close modal, show message)
        } catch (error) {
            // Optionally handle error (e.g., show error message)
        }
        setLoading(false);
    };

    return (
        <div className="container-fluid p-0 m-0 pb-4 modalWrapper">
            <div className="row d-flex justify-content-center p-0 m-0">
                <div className="col-md-12 examModalWrapper p-0 m-0">
                    <div className="d-flex justify-content-between align-items-center px-4 col-md-12 examModalHeader">
                        <h4 style={{ color: "#060317" }} className="fw-bold">
                            {t(" AddClassTest")}
                        </h4>
                        <div className="iconWrapper cursor-pointer" onClick={props.onRequestClose}>
                            <X />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row mt-3 p-0 m-0 px-4">
                <div className="col-md-4 mb-2">
                    <label className="form-label fw-bold" htmlFor="testTypeInput">
                        {t("Exam Type")}
                    </label>
                    <Input
                        id="testTypeInput"
                        value={testType}
                        onChange={e => setTestType(e.target.value)}
                        placeholder={t("Enter Exam Type")}
                        className="p-2"
                    />
                </div>
                <div className="col-md-4 mb-1">
                    <label className="form-label fw-bold" htmlFor="examDatePicker">
                        {t("Exam Date")}
                    </label>
                    <ReactStartEndDatePicker
                        id="examDatePicker"
                        name="examDate"
                        selectedDate={examDate}
                        getDate={handleExamDateChange}
                        minDate={new Date()}
                    />
                </div>
                <div className="col-md-4 mb-2">
                    <label className="form-label fw-bold" htmlFor="totalMarksInput">
                        {t("Total Marks")}
                    </label>
                    <Input
                        id="totalMarksInput"
                        value={totalMarks}
                        onChange={e => setTotalMarks(e.target.value)}
                        placeholder={t("Enter Total Marks")}
                        className="p-2"
                    />
                </div>
            </div>
            <div className="row mt-3 p-0 m-0 px-4 modalBody">
                <Table 
                    columns={columns} 
                    dataSource={results}
                    pagination={false}
                    bordered
                    size="small"
                />
            </div>
            <div className="col-md-12 px-4 mt-3 py-3">
                <div className="d-flex gap-3 justify-content-end">
                    <div onClick={props.onRequestClose}>
                        <FlexBtn label={t("Cancel")} color="#463C77" bgColor="#EDEBF1" />
                    </div>
                    <Button type="primary" loading={loading} onClick={handleSubmitData}>
                        {t("Create")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
export default AddClassResult